import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Drawer, DrawerContent } from '@heroui/react'
import { RefundAction } from '@itsrever/returns-api-types'
import type { ReturnsLineItem } from '@itsrever/rever-api'
import { useTranslate } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useTheme } from '@/ui/theme'
import { RepeatIcon } from '@/ui/icons/repeat'
import { MediaImageIcon } from '@/ui/icons/media-image'
import {
  ExchangedItemType,
  removeExchangeSelectionsByType,
  removeReturnedItem
} from '@/redux/return/return-slice'
import { calculateUnitPrice } from '../helpers/calculate-unit-price'
import { ItemFlow } from '../item-flow/item-flow'

interface MisakoItemCardProps {
  lineItem: ReturnsLineItem
  subProductId: number
}

const CrossButton = ({ onRemove }: { onRemove: () => void }) => (
  <button
    id="crossbox"
    onClick={(e) => {
      e.stopPropagation()
      onRemove()
    }}
    className="flex shrink-0 cursor-pointer items-center justify-center rounded-md bg-white hover:bg-neutral-100"
  >
    <svg
      width="18px"
      height="18px"
      strokeWidth="1"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color="#262626"
      aria-hidden
    >
      <path
        d="M6.75827 17.2426L12.0009 12M17.2435 6.75736L12.0009 12M12.0009 12L6.75827 6.75736M12.0009 12L17.2435 17.2426"
        stroke="#000000"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></path>
    </svg>
  </button>
)

export const MisakoItemCard = ({
  lineItem,
  subProductId
}: MisakoItemCardProps) => {
  const { translate } = useTranslate()

  const { i18n } = useTranslation()

  const theme = useTheme()

  const dispatch = useAppDispatch()

  const [searchParams, setSearchParams] = useSearchParams()

  const [open, setOpen] = useState(false)

  const isFullCatalogOpen = searchParams.get('catalog') === 'true'

  const itemsToReturn = useAppSelector((store) => store.return.returnedItems)

  const exchangeSelections = useAppSelector((store) => store.return.newItems)

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const image = lineItem.product?.images?.[0]

  const price = calculateUnitPrice(
    { quantity: lineItem.quantity, total: lineItem.total },
    { amount_multiplied_by: moneyFormat?.amount_multiplied_by }
  )

  const returnedItem = itemsToReturn.find(
    (retItem) =>
      retItem.item.line_item_id === lineItem.id &&
      retItem.subProductId === subProductId
  )

  const isBeingReturned = !!returnedItem

  const isExchange =
    returnedItem?.item.action === RefundAction.RefundAction_TO_EXCHANGE

  const quantity = returnedItem?.item.quantity

  const itemExchanges = exchangeSelections.filter(
    (item) => item.returnedItemId === returnedItem?.lineItemId
  )

  const variantExchange = itemExchanges.find(
    (item) => item.exchangeType === ExchangedItemType.ONE_ON_ONE
  )

  const fullCatalogExchanges = itemExchanges.filter(
    (item) => item.exchangeType === ExchangedItemType.FULL_CATALOG
  )

  const openItemFlow = (open: boolean) => {
    setOpen(open)

    if (!open && !isFullCatalogOpen) {
      searchParams.delete('lineItemId')
      setSearchParams(searchParams)

      return
    }

    searchParams.set('lineItemId', lineItem.id ?? '')
    setSearchParams(searchParams)
  }

  const onRemoveReturn = () => {
    if (returnedItem?.lineItemId) {
      dispatch(removeReturnedItem({ lineItemId: returnedItem.lineItemId }))
    }
  }

  const onRemoveExchange = (exchangeType: ExchangedItemType) => {
    if (returnedItem?.lineItemId) {
      dispatch(
        removeExchangeSelectionsByType({
          lineItemId: returnedItem.lineItemId,
          exchangeType
        })
      )
    }
  }

  return (
    <>
      <div>
        <div className="flex w-full flex-col gap-4">
          <div
            className="relative h-48 w-36 cursor-pointer overflow-hidden md:h-72 md:w-60"
            onClick={() => {
              if (!isBeingReturned) openItemFlow(true)
            }}
          >
            {image ? (
              <img
                src={image.src}
                width={640}
                height={800}
                alt={image.alt ?? lineItem.name ?? 'Product'}
                className="absolute inset-0 size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center bg-neutral-50 text-neutral-500">
                <MediaImageIcon width={24} height={24} />
              </div>
            )}
          </div>
          <div className="flex max-w-36 flex-col gap-0.5 md:max-w-60">
            <div className="flex w-full flex-col flex-wrap justify-between gap-2 md:flex-row">
              <div className="space-y-1">
                <p className="line-clamp-2 text-xs font-light leading-normal text-neutral-800 md:max-w-44">
                  {lineItem.name}
                </p>
                <p className="text-xs font-light text-neutral-800">
                  {lineItem.variant_name} - {lineItem.product?.sku}
                </p>
              </div>
              <p className="text-xs font-light text-neutral-800">
                {price.toLocaleString(i18n.language, {
                  style: 'currency',
                  currency: moneyFormat?.currency
                })}
              </p>
            </div>
            <p className="mt-1 text-xs font-light text-neutral-800">
              Cantidad {lineItem.quantity}
            </p>
          </div>
        </div>

        {isBeingReturned ? (
          isExchange ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {variantExchange ? (
                <div className="flex w-fit items-center gap-2 border border-solid border-neutral-500 px-1.5 py-1">
                  <CrossButton
                    onRemove={() =>
                      onRemoveExchange(ExchangedItemType.ONE_ON_ONE)
                    }
                  />
                  <span className="shrink-0 text-xs text-neutral-400">
                    {quantity} x
                  </span>
                  <span className="max-w-28 shrink truncate text-xs font-light tracking-wider text-neutral-600">
                    {variantExchange.productVariant.variantName ?? ''}
                  </span>
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    <RepeatIcon color={theme.colors.grey[1]} />
                  </span>
                </div>
              ) : null}
              {fullCatalogExchanges.length > 0 ? (
                <div className="flex w-fit items-center gap-2 border border-solid border-neutral-500 px-1.5 py-1">
                  <CrossButton
                    onRemove={() =>
                      onRemoveExchange(ExchangedItemType.FULL_CATALOG)
                    }
                  />
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    <RepeatIcon color={theme.colors.grey[1]} />
                  </span>
                  <div className="w-24">
                    {fullCatalogExchanges.map((item, index) => (
                      <p key={index} className="truncate text-xs font-light">
                        {item.productVariant.productName ?? ''}
                      </p>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mt-4 flex w-fit items-center gap-1 border border-solid border-neutral-500 px-1.5 py-1">
              <CrossButton onRemove={onRemoveReturn} />
              <span
                className="text-xs font-light uppercase text-neutral-600"
                dangerouslySetInnerHTML={{
                  __html: translate('icons_return')
                }}
              />
            </div>
          )
        ) : null}
      </div>
      <Drawer
        isOpen={open}
        onOpenChange={openItemFlow}
        placement="right"
        radius="none"
        hideCloseButton
        classNames={{
          wrapper: 'h-dvh max-h-dvh',
          backdrop: 'bg-white/30 backdrop-blur-[2px]',
          base: 'rounded-none md:bottom-0 md:right-0 md:top-0 w-full max-w-full md:max-w-[480px] h-dvh max-h-dvh'
        }}
      >
        <DrawerContent>
          {(onClose) => (
            <ItemFlow
              lineItem={lineItem}
              subProductId={subProductId}
              onClose={onClose}
            />
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}
